import BattingPracticeBackground from 'components/images/homePage/battingPracticeBackground.png';
import CalibrationDataCaptureBackground from 'components/images/homePage/calibrationDataCaptureBackground.png';
import LiveDashboardBackground from 'components/images/homePage/liveDashboardBackground.png';
import LiveDashboardFullBackground from 'components/images/homePage/liveDashboardFullBackground.png';
import PitchcomBackground from 'components/images/homePage/pitchComBackground.png';
import { PrimarySystemEndedModal } from 'components/modals/PrimarySystemEndedModal/PrimarySystemEndedModal';
import { SessionEndedModal } from 'components/modals/SessionEndedModal/SessionEndedModal';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionMode } from 'enums/SessionMode';
import { SessionType } from 'enums/SessionType';
import { observer } from 'mobx-react-lite';
import { AbortModal } from 'pages/FieldCalibration/components/AbortModal/AbortModal';
import { CalibrationConfirmModal } from 'pages/FieldCalibration/components/CalibrationConfirmModal/CalibrationConfirmModal';
import { BpDialog } from 'pages/ScheduledGames/BpDialog/BpDialog';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ConfigurationService } from 'services/ConfigurationService';
import { FeatureConfigService } from 'services/FeatureConfigService';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { useInjection } from 'services/ioc/ioc.react';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import { SessionStore } from 'stores/SessionStore';
import { TrackingElementsStore } from 'stores/TrackingElementsStore';
import { UiStore } from 'stores/UiStore';
import VideoUploaderStateStore from 'stores/VideoUploaderStateStore';
import WizardStore from 'stores/WizardStore';
import { canStartNewGame } from '../../services/helper-services/utils';
import { ContextStore } from '../../stores/ContextStore';
import { HomeMenuCard } from './components/HomeMenuCard/HomeMenuCard';
import PitchComModal from './components/PitchComModal/PitchComModal';
import { TagGames } from './components/TagGames/TagGames';
import { CardWrapper, HomePageWrapper } from './HomeStyles';

interface IProps {}

const Home: React.FC<IProps> = observer(() => {
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);
  const configurationService = useInjection<ConfigurationService>(ServiceTypes.ConfigurationService);
  const contextStore = useInjection<ContextStore>(ServiceTypes.ContextStore);
  const wizardStore = useInjection<WizardStore>(ServiceTypes.WizardStore);
  const videoUploaderStateStore = useInjection<VideoUploaderStateStore>(ServiceTypes.VideoUploaderStateStore);
  const featureConfigService = useInjection<FeatureConfigService>(ServiceTypes.FeatureConfigService);
  const config = configurationService.getConfigs();
  const [isVisible, setIsVisible] = useState(false);
  const trackingElementsStore = useInjection<TrackingElementsStore>(ServiceTypes.TrackingElementsStore);
  const uiStore = useInjection<UiStore>(ServiceTypes.UiStore);
  const history = useHistory();

  const allowStartingNewGame = canStartNewGame(
    sessionStore.isGameInProgress,
    !!contextStore.activeField,
    config.isBackfield,
    contextStore.isConnectedPtu,
    contextStore.isLoggedIn,
    trackingElementsStore.updating
  );

  const fetchConfig = async () => {
    await featureConfigService.fetchConfig();
  };

  useEffect(() => {
    if (contextStore.isConnectedPtu) {
      fetchConfig();
    }
  }, [contextStore.isConnectedPtu]);

  useEffect(() => {
    videoUploaderStateStore.setIsVisible(true);

    // Needs to be on every page, so latest sync is set when user changes page
    contextualDataStore.setLatestSync();

    return (): void => {
      videoUploaderStateStore.setIsVisible(false);
    };
  }, []);

  useEffect(() => {
    contextualDataStore.fetchScheduledLocalGames();
  }, [sessionStore.currentSessionMode]);

  useEffect(() => {
    if (
      sessionStore.currentSessionMode.sessionType === SessionType.BouncingBallCalibration &&
      sessionStore.currentSessionMode.sessionMode !== SessionMode.SessionEnded
    ) {
      wizardStore.goToDataCapture();
    } else {
      wizardStore.reset();
    }
  }, [sessionStore.currentSessionMode.sessionType]);

  const goToLiveDashboard = (): void => {
    window.open(config && config.liveDashboardUrl, '_blank');
  };

  const modalDismissHandler = () => {
    setIsVisible(false);
  };

  return (
    <HomePageWrapper>
      <BpDialog
        visible={contextualDataStore.isBpDialogVisible}
        dismissCallback={() => contextualDataStore.setIsBpDialogVisible(false)}
        organizations={mappingHelpers.valuesToDropdownMap(contextualDataStore.owningOrganizations)}
      />
      <TagGames allowStartingNewGame={allowStartingNewGame} />
      <CardWrapper>
        <HomeMenuCard
          backgroundImage={
            featureConfigService.getConfig().isPitchComIntegrationEnabled ? LiveDashboardBackground : LiveDashboardFullBackground
          }
          onClickCallback={goToLiveDashboard}
          height='270px'
          cardClassName='live-dashboard'
          data-test='live-dashboard-tile'
          width={featureConfigService.getConfig().isPitchComIntegrationEnabled ? 'calc(50% - 8px)' : '100%'}
        ></HomeMenuCard>
        {featureConfigService.getConfig().isPitchComIntegrationEnabled && (
          <HomeMenuCard
            onClickCallback={() => setIsVisible(true)}
            backgroundImage={PitchcomBackground}
            height='270px'
            cardClassName='pitchcom-service'
            width='calc(50% - 8px)'
          />
        )}
        <HomeMenuCard
          backgroundImage={BattingPracticeBackground}
          onClickCallback={() => {
            contextualDataStore.setIsBpDialogVisible(true);
          }}
          height='270px'
          cardClassName={`live-dashboard ${sessionStore.isGameInProgress ? 'disabled' : ''}`}
          disabled={!canStartNewGame}
          data-test='batting-practice-tile'
          width='calc(50% - 8px)'
        ></HomeMenuCard>
        <HomeMenuCard
          onClickCallback={() => {
            if (allowStartingNewGame) {
              history.push('/data-capture');
            }
          }}
          backgroundImage={CalibrationDataCaptureBackground}
          height='270px'
          cardClassName='data-capture'
          disabled={!allowStartingNewGame}
          data-test='data-capture-tile'
          width='calc(50% - 8px)'
        ></HomeMenuCard>
        {featureConfigService.getConfig().isPitchComIntegrationEnabled && isVisible && (
          <PitchComModal open={isVisible} onDismiss={modalDismissHandler} />
        )}
        <SessionEndedModal open={uiStore.sessionEndedModalVisible} dismissCallback={() => uiStore.setSessionEndedModalVisible(false)} />
        <PrimarySystemEndedModal
          open={uiStore.primarySystemEndedModalVisible}
          dismissCallback={() => uiStore.setPrimarySystemEndedModalVisible(false)}
        />
      </CardWrapper>
      <AbortModal />
      <CalibrationConfirmModal />
    </HomePageWrapper>
  );
});

export default Home;
